import React, { useState, useEffect, useContext } from "react";
import ThumbsFeedback from "./ThumbsFeedback";
import MetricDisplay from "./MetricDisplay";
import ActionButtons from "./ActionButtons";
import OpportunityDialog from "@/components/dashboard/roadmap/OpportunityDialog";
import InstanceConfigDialog from "./InstanceConfigDialog";
import type { DevTool } from "@/components/dashboard/roadmap/DevToolSelect";
import { getStoredInstanceUrl, getTaskCreationUrl } from "@/components/utils/taskCreationUtils";
import { useToast } from "@/hooks/use-toast";
import { RoadmapContext } from "@/context/RoadmapContext";

interface CompactOpportunityCardProps {
  opportunity: any;
  number?: number;
  cardId: string;
  isArchived?: boolean;
  devTool: DevTool;
}

const CompactOpportunityCard = ({
  opportunity,
  number,
  devTool
}: CompactOpportunityCardProps) => {
  const { meetings, toggleArchiveOpportunity } = useContext(RoadmapContext);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [currentDevTool, setCurrentDevTool] = useState<DevTool>(devTool);
  const { toast } = useToast();
  
  useEffect(() => {
    const handleDevToolChange = (e: CustomEvent) => {
      if (e.detail) {
        setCurrentDevTool(e.detail as DevTool);
      }
    };

    window.addEventListener('devToolChange', handleDevToolChange as EventListener);
    
    return () => {
      window.removeEventListener('devToolChange', handleDevToolChange as EventListener);
    };
  }, []);

  useEffect(() => {
    if (devTool !== currentDevTool) {
      setCurrentDevTool(devTool);
    }
  }, [devTool, currentDevTool]);
  
  const handleSendToDevTool = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    const savedInstanceUrl = getStoredInstanceUrl(currentDevTool);
    
    if (!savedInstanceUrl) {
      setConfigDialogOpen(true);
    } else {
      if (currentDevTool === 'productboard') {
        setConfigDialogOpen(true);
      } else {
        window.open(
          getTaskCreationUrl(
            currentDevTool,
            savedInstanceUrl,
            opportunity.title,
            opportunity.subtitle,
            opportunity.dataPoints
          ),
          '_blank'
        );
        
        toast({
          title: "Link opened",
          description: `Creating a new task in ${currentDevTool.charAt(0).toUpperCase() + currentDevTool.slice(1)}`,
        });
      }
    }
  };

  const handleArchive = async (e: React.MouseEvent) => {
    e.stopPropagation();

    await toggleArchiveOpportunity(opportunity.id)

    toast({
      title: opportunity.isArchived ? "Card restored" : "Card archived",
      description: opportunity.isArchived ? "The card has been restored successfully" : "The card has been archived successfully",
    });
  };

  
  const evidence = Array.isArray(opportunity.Evidence) ? opportunity.Evidence : [];
  let evidenceDistinctMeetings = [...new Set(evidence.map((evidence: any) => evidence.meeting_id))]
  evidenceDistinctMeetings = evidenceDistinctMeetings.filter((meeting) => meeting)
  const evidenceDistinctMeetingsCount = evidenceDistinctMeetings.length
  
  // Count VIP meetings among evidenceDistinctMeetings
  const vipMeetingsCount = evidenceDistinctMeetings.reduce<number>((count, meetingId) => {
    const meeting = meetings.find((m: any) => m.id === meetingId)
    return meeting && meeting.is_vip ? count + 1 : count
  }, 0)
  
  return (
    <>
      <div 
        onClick={() => setIsDialogOpen(true)}
        className="px-6 py-4 border-b border-[#E4E4E4] last:border-b-0 cursor-pointer transition-colors hover:bg-[#FF4A8E]/5 w-full relative"
      >
        <div className="flex items-center gap-2 mb-3">
          {number && (
            <div className="w-6 h-6 bg-[#F5F6F7] rounded-[4px] flex items-center justify-center shrink-0">
              <span className="text-base font-semibold text-[#62718D]">{number}</span>
            </div>
          )}
          <h3 className="font-manrope text-base font-extrabold text-[#40424A]">{opportunity.title}</h3>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex-1 flex items-center justify-between">
            <MetricDisplay
              variant="compact"
              dataPoints={evidenceDistinctMeetingsCount}
              percentage={Math.round((evidenceDistinctMeetingsCount / meetings.length) * 100)}
              vipCount={vipMeetingsCount}
            />

            <ThumbsFeedback size="small" />
          </div>
          
          <ActionButtons 
            onSendToDevTool={handleSendToDevTool}
            onArchive={handleArchive}
            devTool={currentDevTool}
            variant="compact"
            isArchived={opportunity.isArchived}
          />
        </div>
      </div>
      
      <OpportunityDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        opportunity={opportunity}
        number={number}
        onSendToDevTool={handleSendToDevTool}
        devTool={currentDevTool}
      />

      <InstanceConfigDialog
        isOpen={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        devTool={currentDevTool}
        title={opportunity.title}
        description={opportunity.description}
        dataPoints={opportunity.dataPoints}
      />
    </>
  );
};

export default CompactOpportunityCard;
